import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFacility } from '../contexts/FacilityContext';
import { createFacility } from '../services/api';
import { TYPE_COLORS } from '../data/facilities';
import { BILLING_TYPES, BILLING_TYPE_LABELS } from '../utils/billing';
import './Dashboard.css';
import './FacilityHome.css';

const POS_LABELS = {
  '11': 'Office',
  '20': 'Urgent Care Facility',
  '21': 'Inpatient Hospital',
  '22': 'Outpatient Hospital',
  '49': 'Retail Clinic',
  '62': 'Comprehensive Outpatient Rehabilitation Facility',
};

const FACILITY_TYPES = ['Hospital', 'Clinic', 'Urgent Care', 'Rehabilitation', 'Specialty'];

const STATUS_BADGE = {
  suspended: { label: 'Suspended', bg: '#fef3c7', color: '#b45309' },
  cancelled:  { label: 'Cancelled',  bg: '#fee2e2', color: '#b91c1c' },
};

function applyFieldMask(field, value) {
  switch (field) {
    case 'npi':
      return value.replace(/\D/g, '').slice(0, 10);
    case 'taxId': {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    }
    case 'phone':
    case 'fax': {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length >= 7) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length >= 4) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      if (digits.length >= 1) return `(${digits}`;
      return '';
    }
    case 'state':
      return value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
    case 'zip': {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }
    default:
      return value;
  }
}

function Dashboard() {
  const { user } = useAuth();
  const { facilities, facilitiesLoading, facilitiesError, addFacility } = useFacility();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);


  const filteredFacilities = useMemo(() => {
    if (!searchQuery.trim()) return facilities;
    const q = searchQuery.toLowerCase();
    return facilities.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.city.toLowerCase().includes(q) ||
        f.state.toLowerCase().includes(q) ||
        f.type.toLowerCase().includes(q) ||
        f.address.toLowerCase().includes(q)
    );
  }, [searchQuery, facilities]);

  const handleSelectFacility = (facility) => {
    navigate(`/facility/${facility.id}`);
  };

  const openCreate = () => {
    setForm({
      name: '',
      npi: '',
      taxId: '',
      placeOfService: '11',
      type: 'Hospital',
      phone: '',
      fax: '',
      billingType: 'Professional',
      address: '',
      city: '',
      state: '',
      zip: '',
    });
    setCreating(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: applyFieldMask(field, value) }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const data = await createFacility(form);
      addFacility(data.facility);
      setCreating(false);
      navigate(`/facility/${data.facility.id}`);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to create facility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="facility-select-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Welcome back, {user?.firstName || 'User'}
          </h1>
          <p className="page-subtitle">Select a facility to begin billing</p>
        </div>
        <div className="header-actions">
          <div className="stat-chip">
            <span className="stat-chip-number">{facilities.length}</span>
            <span className="stat-chip-label">Facilities</span>
          </div>
          <button className="new-facility-btn" onClick={openCreate}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Facility
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search facilities by name, location, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="search-results-count">
          {filteredFacilities.length} {filteredFacilities.length === 1 ? 'result' : 'results'}
        </span>
      </div>

      {/* Loading / Error States */}
      {facilitiesLoading && (
        <div className="empty-state">
          <div className="fh-spinner" style={{ width: 32, height: 32 }} />
          <p>Loading facilities...</p>
        </div>
      )}
      {!facilitiesLoading && facilitiesError && (
        <div className="empty-state">
          <h3>Unable to load facilities</h3>
          <p>{facilitiesError}</p>
        </div>
      )}

      {/* Facility List + Empty State */}
      {!facilitiesLoading && !facilitiesError && (
        <>
          <div className="facility-grid">
            {filteredFacilities.map((facility) => {
              const facilityStatus = facility.status || 'active';
              const badge = STATUS_BADGE[facilityStatus];
              return (
                <button
                  key={facility.id}
                  className="facility-card"
                  style={facilityStatus === 'cancelled' ? { opacity: 0.55, filter: 'grayscale(40%)' } : undefined}
                  onClick={() => handleSelectFacility(facility)}
                >
                  <div className="facility-card-header">
                    <span
                      className="facility-type-badge"
                      style={{ backgroundColor: TYPE_COLORS[facility.type] || '#171363' }}
                    >
                      {facility.type}
                    </span>
                    {badge && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '2px 9px', borderRadius: 9999,
                        fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        background: badge.bg, color: badge.color,
                      }}>
                        {badge.label}
                      </span>
                    )}
                    <svg className="facility-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                  <h3 className="facility-name">{facility.name}</h3>
                  <div className="facility-location">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{facility.address}, {facility.city}, {facility.state} {facility.zip}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {filteredFacilities.length === 0 && (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <h3>No facilities found</h3>
              <p>Try adjusting your search terms</p>
              <button className="empty-state-btn" onClick={() => setSearchQuery('')}>
                Clear Search
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Facility Modal */}
      {creating && (
        <div className="fh-modal-backdrop">
          <div className="fh-modal">
            <div className="fh-modal-header">
              <div className="fh-modal-header-left">
                <div className="fh-modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <div>
                  <h2 className="fh-modal-title">Create New Facility</h2>
                  <p className="fh-modal-subtitle">Add a new facility to the system</p>
                </div>
              </div>
              <button className="fh-modal-close" onClick={() => setCreating(false)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="fh-edit-form">
              <div className="fh-form-section">
                <span className="fh-form-section-label">General</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field fh-form-full">
                    <label htmlFor="create-name">Facility Name</label>
                    <input id="create-name" type="text" value={form.name} onChange={(e) => handleFormChange('name', e.target.value)} maxLength={100} placeholder="e.g. City Medical Center" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-npi">Facility NPI</label>
                    <input id="create-npi" type="text" value={form.npi} onChange={(e) => handleFormChange('npi', e.target.value)} maxLength={10} placeholder="10-digit NPI" inputMode="numeric" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-taxId">Tax ID (EIN)</label>
                    <input id="create-taxId" type="text" value={form.taxId} onChange={(e) => handleFormChange('taxId', e.target.value)} maxLength={10} placeholder="XX-XXXXXXX" inputMode="numeric" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-type">Facility Type</label>
                    <select id="create-type" value={form.type} onChange={(e) => handleFormChange('type', e.target.value)}>
                      {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-pos">Place of Service</label>
                    <select id="create-pos" value={form.placeOfService} onChange={(e) => handleFormChange('placeOfService', e.target.value)}>
                      {Object.entries(POS_LABELS).map(([code, label]) => (
                        <option key={code} value={code}>{code} — {label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-billing">Billing Type</label>
                    <select id="create-billing" value={form.billingType} onChange={(e) => handleFormChange('billingType', e.target.value)}>
                      {BILLING_TYPES.map((b) => <option key={b} value={b}>{BILLING_TYPE_LABELS[b]}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="fh-form-section">
                <span className="fh-form-section-label">Contact</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field">
                    <label htmlFor="create-phone">Phone</label>
                    <input id="create-phone" type="tel" value={form.phone} onChange={(e) => handleFormChange('phone', e.target.value)} maxLength={14} placeholder="(XXX) XXX-XXXX" />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-fax">Fax</label>
                    <input id="create-fax" type="tel" value={form.fax} onChange={(e) => handleFormChange('fax', e.target.value)} maxLength={14} placeholder="(XXX) XXX-XXXX" />
                  </div>
                </div>
              </div>

              <div className="fh-form-section">
                <span className="fh-form-section-label">Address</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field fh-form-full">
                    <label htmlFor="create-address">Street Address</label>
                    <input id="create-address" type="text" value={form.address} onChange={(e) => handleFormChange('address', e.target.value)} maxLength={150} placeholder="123 Main St" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="create-city">City</label>
                    <input id="create-city" type="text" value={form.city} onChange={(e) => handleFormChange('city', e.target.value)} maxLength={50} placeholder="Dallas" required />
                  </div>
                  <div className="fh-form-field fh-form-half">
                    <label htmlFor="create-state">State</label>
                    <input id="create-state" type="text" value={form.state} onChange={(e) => handleFormChange('state', e.target.value)} maxLength={2} placeholder="TX" required />
                  </div>
                  <div className="fh-form-field fh-form-half">
                    <label htmlFor="create-zip">Zip</label>
                    <input id="create-zip" type="text" value={form.zip} onChange={(e) => handleFormChange('zip', e.target.value)} maxLength={10} placeholder="75201" inputMode="numeric" required />
                  </div>
                </div>
              </div>

              <div className="fh-modal-footer">
                {saveError && <p className="fh-form-error">{saveError}</p>}
                <button type="button" className="fh-btn-cancel" onClick={() => setCreating(false)}>Cancel</button>
                <button type="submit" className="fh-btn-save" disabled={saving}>
                  {saving && <span className="fh-spinner" />}
                  {saving ? 'Creating...' : 'Create Facility'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
